"use client"

import { useState, useEffect } from "react"
import { useLocation, useNavigate } from "react-router-dom"
import { apiService } from "../../services/apiService"

const Assignments = () => {
  const location = useLocation()
  const navigate = useNavigate()
  const queryParams = new URLSearchParams(location.search)
  const studentId = queryParams.get('student')

  const [assignments, setAssignments] = useState([])
  const [filterStatus, setFilterStatus] = useState("all")
  const [showFeedbackForm, setShowFeedbackForm] = useState(false)
  const [selectedAssignment, setSelectedAssignment] = useState(null)
  const [feedback, setFeedback] = useState("")
  const [grade, setGrade] = useState("")
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState("")
  const [successMessage, setSuccessMessage] = useState("")

  useEffect(() => {
    loadAssignments()
  }, [])

  const loadAssignments = async () => {
    try {
      setIsLoading(true)
      setError("")
      const data = await apiService.getAssignments()
      setAssignments(data)
    } catch (error) {
      console.error('Error loading assignments:', error)
      setError(error.message || 'Failed to load assignments')
    } finally {
      setIsLoading(false)
    }
  }

  const handleFilterChange = (e) => {
    setFilterStatus(e.target.value)
  }

  const filteredAssignments = assignments.filter((assignment) => {
    const statusMatch = filterStatus === "all" || assignment.status === filterStatus
    const studentMatch = !studentId || assignment.student_id === Number(studentId)
    return statusMatch && studentMatch
  })

  const openFeedbackForm = (assignment) => {
    setSelectedAssignment(assignment)
    setFeedback(assignment.feedback || "")
    setGrade(assignment.grade || "")
    setShowFeedbackForm(true)
  }

  const handleFeedbackChange = (e) => {
    setFeedback(e.target.value)
  }

  const handleGradeChange = (e) => {
    setGrade(e.target.value)
  }

  const submitFeedback = async (e) => {
    e.preventDefault()

    if (!feedback.trim()) {
      alert("Please enter feedback")
      return
    }

    try {
      await apiService.provideAssignmentFeedback(selectedAssignment.id, {
        feedback,
        grade
      })

      // Update local state
      setAssignments(
        assignments.map((assignment) =>
          assignment.id === selectedAssignment.id 
            ? { ...assignment, feedback, grade, status: "graded" } 
            : assignment
        )
      )

      setShowFeedbackForm(false)
      setSelectedAssignment(null)
      setFeedback("")
      setGrade("")
      setSuccessMessage("Feedback submitted successfully")
      
      // Clear success message after 5 seconds
      setTimeout(() => setSuccessMessage(""), 5000)
    } catch (error) {
      console.error('Error submitting feedback:', error)
      setError(error.message || 'Failed to submit feedback')
    }
  }

  const handleViewDetails = (assignmentId) => {
    navigate(`/mentor/assignments/${assignmentId}`)
  }

  if (isLoading) {
    return <div className="loading">Loading assignments...</div>
  }

  if (error) {
    return <div className="error-message">{error}</div>
  }

  return (
    <div className="assignments-page">
      {studentId && (
        <div className="student-filter-header">
          <button 
            className="btn btn-link" 
            onClick={() => navigate('/mentor/students')}
          >
            <i className="material-icons">arrow_back</i>
            Back to Students
          </button>
          <h2>
            Assignments for {assignments.find(a => a.student_id === Number(studentId))?.student_name}
          </h2>
        </div>
      )}
      
      <div className="filters-container">
        <div className="filter-box">
          <select value={filterStatus} onChange={handleFilterChange} className="form-control">
            <option value="all">All Assignments</option>
            <option value="pending">Pending Review</option>
            <option value="graded">Graded</option>
            <option value="not_submitted">Not Submitted</option>
          </select>
        </div>
      </div>

      {successMessage && (
        <div className="success-message">
          {successMessage}
        </div>
      )}

      {showFeedbackForm && selectedAssignment && (
        <div className="feedback-form-container">
          <div className="form-container">
            <h2 className="form-title">Provide Feedback</h2>
            <form onSubmit={submitFeedback}>
              <div className="form-group">
                <label>Assignment: {selectedAssignment.title}</label>
                <p>Student: {selectedAssignment.student_name}</p>
              </div>

              <div className="form-group">
                <label htmlFor="feedback">Feedback</label>
                <textarea
                  id="feedback"
                  className="form-control"
                  rows="4"
                  value={feedback}
                  onChange={handleFeedbackChange}
                  placeholder="Provide your feedback here..."
                  required
                ></textarea>
              </div>

              <div className="form-group">
                <label htmlFor="grade">Grade</label>
                <select id="grade" className="form-control" value={grade} onChange={handleGradeChange}>
                  <option value="">-- Select a grade --</option>
                  <option value="A">A - Excellent</option>
                  <option value="B">B - Good</option>
                  <option value="C">C - Satisfactory</option>
                  <option value="D">D - Needs Improvement</option>
                  <option value="F">F - Unsatisfactory</option>
                </select>
              </div>

              <div className="form-actions">
                <button type="button" className="btn btn-secondary" onClick={() => setShowFeedbackForm(false)}>
                  Cancel
                </button>
                <button type="submit" className="btn">
                  Submit Feedback
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {filteredAssignments.length > 0 ? (
        filteredAssignments.map((assignment) => (
          <div className="assignment-card" key={assignment.id}>
            <div className="assignment-header">
              <h3 className="assignment-title">{assignment.title}</h3>
              <div className="assignment-meta">
                <span>Due: {new Date(assignment.due_date).toLocaleDateString()}</span>
                <span className={`status-badge status-${assignment.status}`}>
                  {assignment.status === "pending"
                    ? "Pending Review"
                    : assignment.status === "graded"
                      ? "Graded"
                      : "Not Submitted"}
                </span>
              </div>
            </div>

            <div className="assignment-content">
              <p className="assignment-description">{assignment.description}</p>

              <div className="assignment-student">
                <div className="student-small-avatar">
                  <img 
                    src={assignment.student_profile_picture || require('../../images/Untitled.jpeg')} 
                    alt={assignment.student_name} 
                  />
                </div>
                <span>{assignment.student_name}</span>
              </div>

              {assignment.submitted_at && (
                <p className="submission-date">
                  Submitted: {new Date(assignment.submitted_at).toLocaleDateString()}
                </p>
              )}

              {assignment.status === "graded" && (
                <div className="feedback-section">
                  <p className="grade">Grade: {assignment.grade}</p>
                  <p className="feedback">{assignment.feedback}</p>
                </div>
              )}
            </div>

            <div className="assignment-actions">
              {assignment.status === "pending" && (
                <button className="btn" onClick={() => openFeedbackForm(assignment)}>
                  Provide Feedback
                </button>
              )}

              {assignment.status === "graded" && (
                <button className="btn btn-secondary" onClick={() => openFeedbackForm(assignment)}>
                  Edit Feedback
                </button>
              )}

              <button 
                className="btn btn-secondary"
                onClick={() => handleViewDetails(assignment.id)}
              >
                View Details
              </button>
            </div>
          </div>
        ))
      ) : (
        <div className="no-results">
          <p>No assignments found matching your filter criteria.</p>
        </div>
      )}
    </div>
  )
}

export default Assignments
